import { useQuery } from "../_app/lib/query";
import { getMyPermissionForCostCentreUser, getMyPermissions, getPermissions } from "./api";
import { flatten } from "../_app/utils";
import { useFeedbackAlerts } from "../_app/hooks";
import { UserType } from "../user-level/types";
import { useContextProvider } from "../context/providers/ContextProvider";
import { useMyUserLevelType } from "../user-level/hooks";
import { useAuthSession } from "../auth/hooks";

export const permissionCodes = {
  USERS_MANAGE: "USERS_MANAGE",
  BILLING_MANAGE: "BILLING_MANAGE",
  BILLING_DOWNLOAD: "BILLING_DOWNLOAD",
  BILLING_STATEMENT: "BILLING_STATEMENT",
  ACCOUNT_MANAGE: "ACCOUNT_MANAGE",
  ACCOUNT_DPA: "ACCOUNT_DPA",
  ACCOUNT_VIEW_CONTACTS: "ACCOUNT_VIEW_CONTACTS",
  ACCOUNT_CONTACTS: "ACCOUNT_CONTACTS",
  ACCOUNT_PAYMENTS: "ACCOUNT_PAYMENTS",
  ASSET_MANAGEMENT: "ASSET_MANAGEMENT",
  VIEW_ASSETS: "VIEW_ASSETS",
  MOBILE_MANAGEMENT: "MOBILE_MANAGEMENT",
  ALERTS_ACCESS_FEATURE: "ALERTS_ACCESS_FEATURE",
  COST_CENTRE_MANAGE: "COST_CENTRE_MANAGE",
  SUPPORT_ACCESS: "SUPPORT_ACCESS",
  SUPPORT_LOGIC_MONITOR: "SUPPORT_LOGIC_MONITOR",
  COMMUNICATIONS: "COMMUNICATIONS",
};

export function usePermissions(userId?: string, roleId?: string, levelId?: string, options = {}) {
  const { setFeedbackAlertError } = useFeedbackAlerts();
  const { contextId: accountId } = useContextProvider();
  return useQuery({
    queryKey: ["permissions", { accountId, userId, roleId, levelId }],
    queryFn: () => getPermissions({ accountId, userId, roleId, levelId }),
    ...options,

    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message || "Failed to fetch permissions");
    },
  });
}

export function useMyPermissions(contextId: string, options = {}) {
  const { data: session } = useAuthSession();
  const { data: userType } = useMyUserLevelType({ enabled: Boolean(session?.userSub) });
  return useQuery({
    queryKey: ["user-permissions", { userType, contextId }],

    queryFn: () => {
      if (userType === UserType.COST_CENTRE) {
        return getMyPermissionForCostCentreUser(contextId);
      } else {
        return getMyPermissions(contextId);
      }
    },

    enabled: userType !== undefined && Boolean(contextId),
    ...options,
  });
}

export function useHasPermission(targetPermCode: string, options = {}): boolean | undefined {
  const { contextId } = useContextProvider();
  const { data: permissions } = useMyPermissions(contextId, options);
  if (!targetPermCode) return true;
  const perms = permissions || [];
  const flat = perms?.concat(flatten(perms, "permissions"));
  const match = flat?.find((p: any) => p?.code === targetPermCode);
  return match ? Boolean(match?.value) : undefined;
}

export function useHasPermissions(targetPermCodes: string[], options = {}): boolean | undefined {
  const { contextId } = useContextProvider();
  const { data: permissions } = useMyPermissions(contextId, options);
  if (!targetPermCodes || !targetPermCodes.length) return true;
  const perms = permissions || [];
  const flat = perms?.concat(flatten(perms, "permissions"));
  const match = flat?.find((p: any) => targetPermCodes.indexOf(p?.code) !== -1);
  return match ? Boolean(match?.value) : undefined;
}
